/* eslint-disable no-param-reassign */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { User } from 'oidc-client-ts';
import Config from '../app-config';
import { store } from '../app/store';
import { finishRequest, setMessageErrors, startRequest } from '../features/appStateSlice';

export type UserData = {
  access_token?: string;
  token_type?: string;
  refresh_token?: string;
};

export const AXIOS_INSTANCE = axios.create({
  baseURL: Config.domainAdminService.endpoint.replace(/\/$/, ''),
});

let userProvider: (() => UserData | undefined) | null = null;
let silentSignInProvider: (() => Promise<User | null>) | null = null;

export function configureGeneratedClient(
  getUserOrToken: () => UserData | string | null | undefined,
  signinSilent?: () => Promise<User | null>,
): void {
  userProvider = () => {
    const val = getUserOrToken();
    if (!val) return undefined;
    if (typeof val === 'string') {
      return { access_token: val, token_type: 'Bearer' };
    }
    return val;
  };
  if (signinSilent) {
    silentSignInProvider = signinSilent;
  }
}

export function configureTokenRenewer(renewer: () => Promise<string | null>): void {
  silentSignInProvider = async () => {
    const token = await renewer();
    return token ? ({ access_token: token, token_type: 'Bearer' } as User) : null;
  };
}

// The call service client (src/api/callService.ts) talks to a different backend
// through the global axios instance and needs the same bearer token.
export function getAuthToken(): string | null {
  return userProvider ? userProvider()?.access_token ?? null : null;
}

AXIOS_INSTANCE.interceptors.request.use(
  async (config) => {
    if (!(config as any)?.skipLoadingOverlay) {
      store.dispatch(startRequest());
    }
    if (userProvider) {
      if (!userProvider()?.access_token && silentSignInProvider) {
        try {
          await silentSignInProvider();
        } catch {
          // Handled by AuthenticationProvider
        }
      }
      const { access_token: accessToken, token_type: tokenType = 'Bearer' } = userProvider() || {};
      if (accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: `${tokenType} ${accessToken}`,
        };
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Extracts a human-readable message from an HTTP error response.
 * Understands RFC 7807 problem details, ASP.NET validation errors and
 * a couple of non-standard shapes. (Ported from TenantUI masterdataClient.)
 */
export const extractProblemMessage = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError<Record<string, unknown>>;
  const data = axiosError?.response?.data;
  if (!data) return undefined;

  if (typeof data === 'string') return data || undefined;

  // RFC 7807: prefer `detail`, then `title`
  if (typeof data.detail === 'string' && data.detail) return data.detail;

  // ASP.NET validation errors dict: { errors: { Field: ["message"] } }
  if (data.errors && typeof data.errors === 'object') {
    const messages = Object.entries(data.errors as Record<string, string[]>)
      .map(([field, fieldMessages]) =>
        Array.isArray(fieldMessages) ? `${field}: ${fieldMessages[0]}` : null,
      )
      .filter((m): m is string => m != null);
    if (messages.length > 0) return messages.join(', ');
  }

  if (typeof data.title === 'string' && data.title) return data.title;

  // Non-standard shapes e.g. {"error":"..."} or {"message":"..."}
  if (typeof data.error === 'string' && data.error) return data.error;
  if (typeof data.message === 'string' && data.message) return data.message;

  return undefined;
};

// Mutator function that Orval calls for every request
export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  AXIOS_INSTANCE(config).then((res) => res.data);

// Automatically enriches every rejected response:
//  1. Sets error.message to the backend's human-readable string.
//  2. Dispatches setMessageErrors so the global snackbar fires without
//     any try/catch at call sites. Call sites that need special handling
//     can still catch and override.
const handleResponseError = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config as
    | (AxiosRequestConfig & { retry?: boolean; skipLoadingOverlay?: boolean })
    | undefined;

  // If request failed with 401 and hasn't been retried yet, attempt silent renew
  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest.retry &&
    silentSignInProvider
  ) {
    originalRequest.retry = true;
    try {
      const user = await silentSignInProvider();
      if (user?.access_token) {
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `${user.token_type || 'Bearer'} ${user.access_token}`,
        };
        return await AXIOS_INSTANCE(originalRequest);
      }
    } catch {
      // If silent renew fails, proceed to error dispatch
    }
  }

  if (!originalRequest?.skipLoadingOverlay) {
    store.dispatch(finishRequest());
  }

  const problemMessage = extractProblemMessage(error);
  if (problemMessage) {
    error.message = problemMessage;
  }

  const isMeEndpoint = error.config?.url?.includes('/me');
  if (!isMeEndpoint && !(error.config as any)?.skipGlobalErrorToast) {
    store.dispatch(setMessageErrors([error.message || 'An unexpected error occurred']));
  }
  return Promise.reject(error);
};

AXIOS_INSTANCE.interceptors.response.use((response) => {
  if (!(response.config as any)?.skipLoadingOverlay) {
    store.dispatch(finishRequest());
  }
  return response;
}, handleResponseError);

// Also cover callService, which uses the global axios instance
axios.interceptors.request.use(
  (config) => {
    if (!(config as any)?.skipLoadingOverlay) {
      store.dispatch(startRequest());
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use((response) => {
  if (!(response.config as any)?.skipLoadingOverlay) {
    store.dispatch(finishRequest());
  }
  return response;
}, handleResponseError);
